import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { UserProfile, UserRole } from '@/types';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  error: string | null;
}

interface UseAuthReturn extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, referralCode?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<UseAuthReturn | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthInternal();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function useAuthInternal(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    role: null,
    loading: true,
    error: null,
  });

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[useAuth] Auth state changed:', user ? `User: ${user.email}` : 'No user');

      if (user) {
        try {
          console.log('[useAuth] Fetching profile for uid:', user.uid);
          // Fetch user profile
          const profileDoc = await getDoc(doc(db, 'users', user.uid));
          const profile = profileDoc.exists() ? (profileDoc.data() as UserProfile) : null;
          console.log('[useAuth] Profile fetched:', profile ? 'exists' : 'missing');

          console.log('[useAuth] Fetching role for uid:', user.uid);
          // Fetch user role
          const roleDoc = await getDoc(doc(db, 'userRoles', user.uid));
          const role = roleDoc.exists() ? (roleDoc.data().role as UserRole) : 'user';
          console.log('[useAuth] Role fetched:', role);

          setState({
            user,
            profile,
            role,
            loading: false,
            error: null,
          });
          console.log('[useAuth] State updated successfully');
        } catch (error) {
          console.error('[useAuth] Error fetching user data:', error);
          // Set state anyway to stop loading
          setState({
            user,
            profile: null,
            role: 'user',
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load user data',
          });
        }
      } else {
        console.log('[useAuth] No user, setting empty state');
        setState({
          user: null,
          profile: null,
          role: null,
          loading: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName: string, referralCode?: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // Create auth user
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      await updateProfile(user, { displayName });

      // Generate unique referral code for this user (lowercase displayName)
      const userReferralCode = displayName.replace(/\s+/g, '').toLowerCase();

      // Create user profile document
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName,
        referralCode: userReferralCode,
        referredBy: referralCode || null,
        referralCredits: 0,
        totalReferrals: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Set default role
      await setDoc(doc(db, 'userRoles', user.uid), {
        userId: user.uid,
        role: 'user',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // If user was referred, create referral record and credit the referrer
      if (referralCode) {
        try {
          // Find the referrer by referralCode
          const usersSnapshot = await getDoc(doc(db, 'users', user.uid)); // This is a placeholder
          // Note: We'll need to query by referralCode to find the referrer
          // For now, we'll create the referral document with the code

          await setDoc(doc(db, 'referrals', user.uid), {
            newUserId: user.uid,
            newUserEmail: user.email,
            newUserName: displayName,
            referralCode: referralCode,
            credited: false, // Will be set to true when referrer gets credit
            createdAt: serverTimestamp(),
          });
        } catch (referralError) {
          console.error('Failed to process referral:', referralError);
          // Don't fail signup if referral processing fails
        }
      }

      // Send verification email
      await sendEmailVerification(user);

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      setState((prev) => ({ ...prev, error: message }));
      throw error;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send reset email';
      setState((prev) => ({ ...prev, error: message }));
      throw error;
    }
  }, []);

  const resendVerification = useCallback(async () => {
    if (state.user && !state.user.emailVerified) {
      try {
        await sendEmailVerification(state.user);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send verification email';
        setState((prev) => ({ ...prev, error: message }));
        throw error;
      }
    }
  }, [state.user]);

  const updateUserProfile = useCallback(async (data: Partial<UserProfile>) => {
    if (!state.user) throw new Error('No user logged in');
    
    try {
      await setDoc(
        doc(db, 'users', state.user.uid),
        {
          ...data,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      
      // Update local state
      setState((prev) => ({
        ...prev,
        profile: prev.profile ? { ...prev.profile, ...data } : null,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update profile';
      setState((prev) => ({ ...prev, error: message }));
      throw error;
    }
  }, [state.user]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
    resendVerification,
    updateUserProfile,
    clearError,
  };
}
